import { useEffect } from "react";
import { useLocation } from "react-router";
import { useMe } from "@/api/me";

// Ordered longest-prefix-first so `/settings/my-profile` falls under
// "Settings" before any shorter prefix could match.
const ROUTE_LABELS: Array<[string, string]> = [
  ["/getting-started", "Getting Started"],
  ["/my-plants", "My Plants"],
  ["/overview", "Overview"],
  ["/notifications", "Notifications"],
  ["/settings", "Settings"],
  ["/reports", "Reports"],
  ["/tickets", "Tickets"],
  ["/simulator", "Simulator"],
  ["/quote", "Quote"],
  ["/admin/developer", "Developer"],
  ["/admin/platform", "Platform"],
  ["/alarms", "Alarms"],
  ["/sites", "Sites"],
  ["/admin", "Admin"],
  ["/inbox", "Inbox"],
  ["/map", "Map"],
  ["/ml", "MLOps"],
];

/**
 * Updates document.title to "<Page> · <AppName> · Monitoring" on every
 * navigation. Falls back to "<AppName> · Monitoring" (or just "Monitoring")
 * when the current route doesn't match a known page label.
 *
 * Must be called inside the router context (e.g. AppShell) so useLocation
 * is available. BrandingProvider sets the initial title outside the router;
 * this hook overrides it per-page on every navigation.
 */
export function usePageTitle(): void {
  const { pathname } = useLocation();
  const { data } = useMe();
  const appName = data?.branding?.app_name;

  useEffect(() => {
    const match = ROUTE_LABELS.find(([prefix]) => pathname.startsWith(prefix));
    const pageLabel = match?.[1];
    const base = appName ? `${appName} · Monitoring` : "Monitoring";
    document.title = pageLabel ? `${pageLabel} · ${base}` : base;
  }, [pathname, appName]);
}
